import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Col,
  Row,
  Checkbox,
  Button,
  message,
  Select,
  Alert,
  Tooltip,
  ConfigProvider,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { validateMobileNumber } from '../../Validators/ValidatorMobileNumber';
import { validateEmail } from '../../Validators/ValidatorEmail';
import {
  validateAtLeastOneStudent,
  validateCompanyName,
} from '../../Validators/ValidatorOrganization';

import '../../Assets/Styles/Form.module.css';
import {
  GetCurrentStudyYear,
  GetOrganizations,
  PostOrganization,
} from '../../helper/apiHelper';
import type { StudyYear } from '../../Types/interfaces';
import { PaperWorkLanguage, type Organization } from '../../Types/interfaces';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useProcessStartSettings } from '../../hooks/useProcessStartSettings';
import { ProcessBlockedBanner } from '../ProcessBlockedBanner';
import { PaperWorkLanguageNotice } from '../PaperWorkLanguageNotice';

const { TextArea } = Input;

interface OrganizationFormValues {
  representativeName: string;
  name: string;
  description: string;
  city: string;
  county: string;
  street: string;
  streetNumber: string;
  phoneNumber: string;
  email: string;
  fax: string;
  fiscalCode: string;
  fiscalAccount: string;
  iban: string;
  isRomanian: boolean;
  bachelorStudents: string;
  masterStudents: string;
  paperWorkType: PaperWorkLanguage;
}

const OrganizationFormComponent = (): React.ReactElement => {
  const loggedInUser = useCurrentUser();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const queryClient = useQueryClient();
  const [studyYear, setStudyYear] = useState<StudyYear | null>();

  useEffect(() => {
    const fetchData = async () => {
      const resp = await GetCurrentStudyYear();
      setStudyYear(resp ? resp : null);
    };
    fetchData();
  }, []);

  const postOrganizationMutation = useMutation({
    mutationFn: PostOrganization,
    onSuccess: () => {
      form.resetFields();
      messageApi.success({
        content: t('orgRegComp.regSuccess'),
      });

      // invalidate organization cached data
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.ORGANIZATIONS,
      });

      // invalidate user cached data
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.USER,
      });
    },
    onError: () => {
      messageApi.error({
        content: t('orgRegComp.regError'),
      });
    },
  });

  const { data: organizations } = useQuery({
    queryKey: [...QUERY_KEYS.ORGANIZATIONS],
    queryFn: async () => {
      return await GetOrganizations();
    },
    initialData: [],
  });

  const handleSubmit = async (org: OrganizationFormValues): Promise<void> => {
    if (!studyYear) {
      return;
    }

    const organization: Organization = {
      representativeId: loggedInUser?.id ?? 0,
      representativeName: org.representativeName,
      name: org.name,
      description: org.description,
      city: org.city,
      county: org.county,
      street: org.street,
      streetNumber: org.streetNumber,
      phoneNumber: org.phoneNumber,
      email: org.email,
      fax: org.fax,
      fiscalCode: org.fiscalCode,
      fiscalAccount: org.fiscalAccount,
      iban: org.iban,
      isRomanian: org.isRomanian === undefined || !!org.isRomanian,
      bachelorStudents: parseInt(org.bachelorStudents) || 0,
      masterStudents: parseInt(org.masterStudents) || 0,
      language: org.paperWorkType,
      studyYearId: studyYear.id,
    };

    form.resetFields();

    // update organizations
    await postOrganizationMutation.mutateAsync(organization);
  };

  const { companyRegistrationEnabled } = useProcessStartSettings();
  const selectedPaperWorkType = Form.useWatch('paperWorkType', form);

  return (
    <>
      {contextHolder}
      {!companyRegistrationEnabled && (
        <ProcessBlockedBanner
          style={{ marginBottom: 16 }}
          message={t('processStartSettings.companyRegistrationBlocked')}
        />
      )}
      <PaperWorkLanguageNotice
        language={selectedPaperWorkType}
        variant="organization"
        style={{ marginBottom: 16 }}
      />
      {studyYear === null && (
        <Alert
          message={t('orgRegComp.alertMessage')}
          description={t('orgRegComp.alertDescription')}
          type="error"
          showIcon
        />
      )}
      <ConfigProvider
        theme={{ components: { Form: { verticalLabelPadding: '0 0 2px' } } }}
      >
        <Form
          layout="vertical"
          initialValues={{
            paperWorkType: PaperWorkLanguage[PaperWorkLanguage.Romanian],
          }}
          size="large"
          onFinish={handleSubmit}
          form={form}
          style={{ paddingTop: 10 }}
        >
          <Col>
            <Row justify="space-between" gutter={10}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  label={t('orgRegComp.orgName')}
                  tooltip={t('orgRegComp.orgNameHelp')}
                  rules={[
                    {
                      required: true,
                      message: t('orgRegComp.companyNameNeeded'),
                    },
                    {
                      validator: (rule, value) =>
                        validateCompanyName(rule, value, organizations),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="representativeName"
                  label={t('orgRegComp.representativeName')}
                  tooltip={t('orgRegComp.representativeHelp')}
                  rules={[
                    {
                      required: true,
                      message: t('orgRegComp.representativeNameNeeded'),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row justify="space-between" gutter={10}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="city"
                  label={t('orgRegComp.city')}
                  rules={[
                    { required: true, message: t('orgRegComp.cityNeeded') },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="county"
                  label={t('orgRegComp.county')}
                  rules={[
                    { required: true, message: t('orgRegComp.countyNeeded') },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="street"
                  label={t('orgRegComp.street')}
                  rules={[
                    { required: true, message: t('orgRegComp.streetNeeded') },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="streetNumber"
                  label={t('orgRegComp.streetNr')}
                  rules={[
                    {
                      required: true,
                      message: t('orgRegComp.numberNeeded'),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="description"
              label={t('orgRegComp.desc')}
              tooltip={t('orgRegComp.descriptionHelp')}
              rules={[
                { required: true, message: t('orgRegComp.descriptionNeeded') },
                { min: 20, message: t('orgRegComp.descriptionMinLength') },
              ]}
            >
              <TextArea rows={4} />
            </Form.Item>
            <Row justify="space-between" gutter={10}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="phoneNumber"
                  label={t('orgRegComp.phone')}
                  tooltip={t('orgRegComp.phoneHelp')}
                  rules={[
                    {
                      required: true,
                      message: t('orgRegComp.phoneNeeded'),
                    },
                    { validator: validateMobileNumber },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="fax"
                  label={t('orgRegComp.fax')}
                  rules={[{ message: t('orgRegComp.faxNeeded') }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="email"
                  label={t('orgRegComp.email')}
                  tooltip={t('orgRegComp.emailHelp')}
                  rules={[
                    {
                      required: true,
                      message: t('orgRegComp.emailNeeded'),
                    },
                    { validator: validateEmail },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row justify="space-between" gutter={10}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="fiscalCode"
                  label={t('orgRegComp.fisc')}
                  tooltip={t('orgRegComp.fiscalCodeHelp')}
                  rules={[
                    {
                      required: true,
                      message: t('orgRegComp.fiscalCodeNeeded'),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="fiscalAccount"
                  label={t('orgRegComp.account')}
                  tooltip={t('orgRegComp.bankHelp')}
                  rules={[
                    {
                      required: true,
                      message: t('orgRegComp.bankAccountNeeded'),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="iban"
                  label={t('orgRegComp.iban')}
                  tooltip={t('orgRegComp.ibanHelp')}
                  rules={[
                    { required: true, message: t('orgRegComp.ibanNeeded') },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row justify="space-between" gutter={10}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="bachelorStudents"
                  label={t('orgRegComp.bachelorStudents')}
                  tooltip={t('orgRegComp.studentCountHint')}
                  dependencies={['masterStudents']}
                  rules={[
                    {
                      required: true,
                      message: t('orgRegComp.bachelorStudentsNeeded'),
                    },
                    {
                      type: 'number',
                      min: 0,
                      message: t('orgRegComp.nonNegativeNumber'),
                    },
                    validateAtLeastOneStudent('masterStudents', t),
                  ]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="masterStudents"
                  label={t('orgRegComp.masterStudents')}
                  tooltip={t('orgRegComp.studentCountHint')}
                  dependencies={['bachelorStudents']}
                  rules={[
                    {
                      required: true,
                      message: t('orgRegComp.masterStudentsNeeded'),
                    },
                    {
                      type: 'number',
                      min: 0,
                      message: t('orgRegComp.nonNegativeNumber'),
                    },
                    validateAtLeastOneStudent('bachelorStudents', t),
                  ]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="paperWorkType"
                  label={t('orgRegComp.paperWorkLanguage')}
                  tooltip={t('orgRegComp.languageHelp')}
                  rules={[
                    {
                      required: true,
                      message: t('orgRegComp.languageRequired'),
                    },
                  ]}
                >
                  <Select
                    options={Object.values(PaperWorkLanguage)
                      .filter((val) => typeof val !== 'number')
                      .map((value) => ({
                        label: t(`orgRegComp.paperWorkLanguages.${value}`),
                        value,
                      }))}
                  ></Select>
                </Form.Item>
              </Col>
              <Col span={7}></Col>
            </Row>

            <Row justify="space-between">
              <Col span={7}>
                <Form.Item name="isRomanian" valuePropName="checked">
                  <Checkbox>
                    {t('orgRegComp.fromRom')}{' '}
                    <Tooltip title={t('orgRegComp.isRomanianHelp')}>
                      <InfoCircleOutlined style={{ opacity: 0.45 }} />
                    </Tooltip>
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={postOrganizationMutation.isPending}
                disabled={studyYear === null || !companyRegistrationEnabled}
              >
                {t('orgRegComp.register')}
              </Button>
            </Form.Item>
          </Col>
        </Form>
      </ConfigProvider>
    </>
  );
};

export default OrganizationFormComponent;
